import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Clock, ChevronRight, Sun, Waves, Utensils, Home } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DestinationsPage = () => {
  const [destinations, setDestinations] = useState([]);

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await axios.get(`${API}/destinations`);
        setDestinations(response.data);
      } catch (error) {
        console.error('Error fetching destinations:', error);
      }
    };
    fetchDestinations();
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const algarve = destinations.find(d => d.id === 'algarve');
  const futureDestinations = destinations.filter(d => d.id !== 'algarve');

  const algarveHighlights = [
    {
      title: 'Top 20 Portugal',
      description: 'Coached by one of the best padel players in the country.',
    },
    {
      title: 'FIP Certified',
      description: 'International Padel Federation certified coaching and training methods.',
    },
    {
      title: '+8 Years Experience',
      description: 'Professional coaching experience with players of all levels.',
    },
    {
      title: 'Personalised Approach',
      description: 'Every session tailored to your goals and playing style.',
    },
  ];

  return (
    <div className="bg-[#f5f3ee]" data-testid="destinations-page">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-24" data-testid="destinations-hero">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_global-padel-2/artifacts/0bqdaba0_WhatsApp%20Image%202026-02-07%20at%2012.44.21.jpeg"
            alt="Portimão aerial view"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-5 py-2 border border-[#f5f3ee]/40 text-[#f5f3ee] text-xs tracking-[0.3em] uppercase mb-8">
              Our Destinations
            </span>
            <h1 className="font-heading font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#f5f3ee] mb-6">
              Where Padel Meets<br />
              <span className="italic">Paradise</span>
            </h1>
            <p className="text-lg text-[#f5f3ee]/80 max-w-xl mx-auto font-light">
              Currently featuring the stunning Algarve, with more exciting destinations coming soon.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Algarve Feature Section */}
      <section className="py-24 md:py-32" data-testid="algarve-feature">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp} className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <MapPin className="text-[#8ca87c]" size={18} />
                <span className="text-[#414b3b]/60 font-light tracking-wider uppercase text-sm">Portugal</span>
              </div>
              <h2 className="font-heading font-light text-4xl sm:text-5xl text-[#414b3b] mb-6">
                Algarve
              </h2>
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="px-4 py-2 bg-white text-[#414b3b]/70 text-xs tracking-wider uppercase">
                  All Levels
                </span>
                <span className="px-4 py-2 bg-white text-[#414b3b]/70 text-xs tracking-wider uppercase flex items-center gap-1">
                  <Clock size={12} />
                  3-7 days
                </span>
                <span className="px-4 py-2 bg-[#8ca87c] text-[#f5f3ee] text-xs tracking-wider uppercase">
                  Now Available
                </span>
              </div>
              <p className="text-base text-[#414b3b]/60 mb-8 font-light leading-relaxed">
                Experience padel in the beautiful Algarve with a coach who competes at the highest level. 
                As a Top 20 player in Portugal and FIP certified, I'll help you elevate your game while 
                enjoying everything this stunning region has to offer.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-10">
                {algarveHighlights.map((highlight, index) => (
                  <div key={index} className="bg-white p-5" data-testid={`algarve-highlight-${index}`}>
                    <span className="text-3xl font-heading font-light text-[#8ca87c]/40 mb-2 block">0{index + 1}</span>
                    <h4 className="font-heading text-[#414b3b] mb-1">
                      {highlight.title}
                    </h4>
                    <p className="text-sm text-[#414b3b]/50 font-light">
                      {highlight.description}
                    </p>
                  </div>
                ))}
              </div>

              <Link
                to="/experiences"
                className="inline-flex items-center gap-3 h-14 px-8 bg-[#414b3b] text-[#f5f3ee] font-light tracking-[0.15em] uppercase text-sm transition-all duration-300 hover:bg-[#8ca87c]"
                data-testid="see-packages-btn"
              >
                See All Packages
                <ChevronRight size={18} />
              </Link>
            </div>

            {/* Image Grid */}
            <div className="relative">
              <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[600px]">
                <div className="col-span-4 row-span-4 overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_elegant-padel-1/artifacts/03w78olq_WhatsApp%20Image%202026-03-11%20at%2017.05.03.jpeg"
                    alt="FIP Tournament"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="col-span-2 row-span-3 overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_global-padel-2/artifacts/nj2h5lfb_WhatsApp%20Image%202026-02-07%20at%2012.36.19.jpeg"
                    alt="Carvoeiro beach"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="col-span-2 row-span-3 overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_global-padel-2/artifacts/vy8m399l_WhatsApp%20Image%202026-02-07%20at%2012.40.45.jpeg"
                    alt="Padel courts at sunset"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="col-span-4 row-span-2 overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_global-padel-2/artifacts/1ng4petn_472654994.jpg"
                    alt="Algarve coastline"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Coming Soon Destinations */}
      <section className="py-24 md:py-32 bg-white" data-testid="coming-soon">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
              Expanding Soon
            </span>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-6">
              More Destinations Coming
            </h2>
            <p className="text-base text-[#414b3b]/60 max-w-2xl mx-auto font-light">
              We're working on bringing padel experiences to more incredible locations.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {futureDestinations.map((destination, index) => (
              <motion.div
                key={destination.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative group overflow-hidden aspect-[16/9]"
                data-testid={`coming-destination-${index}`}
              >
                <img
                  src={destination.image_url}
                  alt={destination.name}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#414b3b]/90 via-[#414b3b]/40 to-transparent" />
                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  <span className="inline-block self-start px-3 py-1 bg-[#414b3b]/60 text-[#f5f3ee] text-xs tracking-wider uppercase mb-3">
                    Coming Soon
                  </span>
                  <h3 className="font-heading text-2xl text-[#f5f3ee] mb-2">
                    {destination.name}
                  </h3>
                  <p className="text-[#f5f3ee]/70 text-sm flex items-center gap-1 font-light">
                    <MapPin size={14} />
                    {destination.country}
                  </p>
                  <p className="text-[#f5f3ee]/50 mt-3 line-clamp-2 font-light text-sm">
                    {destination.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#8ca87c]" data-testid="destinations-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-6">
              Start Your Algarve Adventure
            </h2>
            <p className="text-base text-[#414b3b]/70 mb-10 font-light">
              Book now and experience the best padel destination in Europe.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 h-14 px-10 bg-[#414b3b] text-[#f5f3ee] font-light tracking-[0.15em] uppercase text-sm transition-all duration-300 hover:bg-[#333d2e]"
              data-testid="destinations-cta-btn"
            >
              Book Your Trip
              <ChevronRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DestinationsPage;
